import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider, DataTableHeader, DataTableHeaderGroup, DataTableHead, DataTableBody, DataTableRow, DataTableCell,
} from "@/components/ui/data-table";
import type { ListPagination } from "./useRemoteList";
import { GridProvider, GridToolbar, PaginationBar, StatsStrip, type SortOption, type Stat } from "./DataGrid";

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

/** Toolbar, statistics and export settings for the grid layout (see DataGrid). */
export interface GridOptions {
  /** Remembers Customize / Show statistics per page. */
  id: string;
  view?: ReactNode;
  search?: ReactNode;
  filters?: ReactNode;
  activeFilters?: number;
  onClearFilters?: () => void;
  sortOptions?: SortOption[];
  sort?: string;
  onSort?: (value: string) => void;
  stats?: Stat[];
  statsLoading?: boolean;
  exportEntity?: string;
  exportPermission?: string;
}

/**
 * The list layout used by every list page. With `grid`, it renders the full data-grid layout:
 * toolbar (view, filter, sort, statistics, customize, export, add), a statistics strip, then the
 * table (or cards) with a pagination footer (rows per page, numbered pages, go to page).
 * Without `grid`, the older single-card layout with an inline `toolbar` is kept.
 * Pass `renderCard` to lay rows out as cards, or `renderBody` for full control.
 */
export default function ListShell<T>({
  columns = [], rows, loading, toolbar, grid, empty, onAdd, addLabel, onDeleteSelected, onRowClick, pagination, onPage, onPerPage, noun = "records", renderCard, renderBody, rowKey, cardClass,
}: {
  columns?: ColumnDef<T>[];
  renderCard?: (row: T) => ReactNode;
  /** Full control over how the loaded rows are laid out (e.g. grouped sections). */
  renderBody?: (rows: T[]) => ReactNode;
  /** Extra classes for one card, e.g. to highlight it. */
  cardClass?: (row: T) => string;
  rowKey?: (row: T) => string;
  rows: T[];
  loading: boolean;
  toolbar?: ReactNode;
  grid?: GridOptions;
  empty: { icon: React.ComponentType<{ className?: string }>; title: string; text: string; action?: string };
  onAdd?: () => void;
  addLabel?: string;
  onDeleteSelected?: (rows: T[]) => void;
  onRowClick?: (row: T) => void;
  pagination?: ListPagination;
  onPage?: (page: number) => void;
  onPerPage?: (perPage: number) => void;
  noun?: string;
}) {
  const Icon = empty.icon;
  const body = (
    <>
      {loading ? (
        <SkeletonRow />
      ) : (
        <>
          {renderBody ? (
            rows.length > 0 && renderBody(rows)
          ) : renderCard ? (
            rows.length > 0 && (
              <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {rows.map((row, i) => (
                  <div
                    key={rowKey ? rowKey(row) : i}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={`rounded-xl border border-gray-200 bg-white p-4 transition hover:border-primary/40 hover:shadow-sm ${onRowClick ? "cursor-pointer" : ""} ${cardClass?.(row) ?? ""}`}
                  >
                    {renderCard(row)}
                  </div>
                ))}
              </div>
            )
          ) : (
            <TableProvider columns={columns} data={rows} onDeleteSelected={onDeleteSelected} selectable={!!onDeleteSelected || !!grid} onAddRow={grid ? undefined : onAdd} addRowLabel={addLabel}>
              <DataTableHeader>
                {({ headerGroup }) => (
                  <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                    {({ header }) => <DataTableHead key={header.id} header={header} />}
                  </DataTableHeaderGroup>
                )}
              </DataTableHeader>
              <DataTableBody>
                {({ row }) => (
                  <DataTableRow key={row.id} row={row} className={onRowClick ? "cursor-pointer" : ""} onClick={onRowClick ? () => onRowClick(row.original as T) : undefined}>
                    {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                  </DataTableRow>
                )}
              </DataTableBody>
            </TableProvider>
          )}

          {rows.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">{empty.title}</h3>
              <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">{empty.text}</p>
              {onAdd && empty.action && (
                <button type="button" onClick={onAdd} className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200">
                  {empty.action}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {pagination && onPage && (grid || pagination.total_pages > 1) && (
        <PaginationBar page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} perPage={pagination.per_page} onPage={onPage} onPerPage={onPerPage} noun={noun} />
      )}
    </>
  );

  if (!grid) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {toolbar && <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">{toolbar}</div>}
        {body}
      </div>
    );
  }

  return (
    <GridProvider id={grid.id}>
      <div className="space-y-4">
        <GridToolbar
          view={grid.view} search={grid.search} filters={grid.filters} activeFilters={grid.activeFilters} onClearFilters={grid.onClearFilters}
          sortOptions={grid.sortOptions} sort={grid.sort} onSort={grid.onSort} hasStats={!!grid.stats?.length}
          exportEntity={grid.exportEntity} exportPermission={grid.exportPermission} onAdd={onAdd} addLabel={addLabel}
        />
        {grid.stats && <StatsStrip items={grid.stats} loading={grid.statsLoading} />}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">{body}</div>
      </div>
    </GridProvider>
  );
}

export const toolbarInput = "px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";

export const addButton = "px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200 min-w-[140px]";

export const pill = (cls: string) => `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`;

/** A person's picture when they have one, otherwise their initials in a round badge. */
export const Avatar = ({ name, src, className = "" }: { name?: string | null; src?: string | null; className?: string }) => src ? (
  <img src={src} alt={name ?? ""} className={`h-10 w-10 shrink-0 rounded-full object-cover ${className}`} />
) : (
  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary ${className}`}>
    {(name ?? "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "—"}
  </span>
);

/** Stops a link or button inside a clickable card from also opening the card. */
export const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
